import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  MdCalendarToday,
  MdAssignment,
  MdDriveEta,
  MdSchool,
  MdPeople,
} from "react-icons/md";

import bannerImg from "../../assets/images/logo-fullwidth.png";

import "./Home.scss";

const studentFeatures = [
  {
    icon: MdCalendarToday,
    title: "Book your lessons",
    description:
      "Choose dates and times that work for you. See your upcoming driving lessons in one place and stay on top of your schedule.",
  },
  {
    icon: MdAssignment,
    title: "Track your progress",
    description:
      "View feedback from your instructor so you know how you’re doing and what to focus on next.",
  },
  {
    icon: MdDriveEta,
    title: "Manage your profile",
    description:
      "Keep your details up to date and access everything you need for your driving program in a single dashboard.",
  },
];

const otherRoles = [
  {
    icon: MdSchool,
    title: "Instructors",
    summary:
      "Manage your calendar, log sessions, and work with private students—all in one place.",
  },
  {
    icon: MdPeople,
    title: "Administrators",
    summary:
      "Oversee users, appointments, session logs, and payroll from a single dashboard.",
  },
];

const Home = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);

  return (
    <div className="home-page">
      <section className="home-hero">
        <div className="home-hero__inner">
          <div className="home-hero__brand">
            <img
              src={bannerImg}
              alt="New Drivers"
              className="home-hero__logo"
            />
          </div>
          <h1 className="home-hero__title">
            Book lessons.{" "}
            <span className="home-hero__accent">Track progress.</span> Get on
            the road.
          </h1>
          <p className="home-hero__lead">
            The student portal for New Drivers—schedule your driving lessons,
            see your session history, and manage your profile in one place.
            Instructors and admins use the same platform for calendars, logs,
            and school management.
          </p>
          <div className="home-hero__cta">
            {isAuthenticated ? (
              <Link to="/profile" className="home-cta home-cta--primary">
                Go to dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="home-cta home-cta--primary">
                  Sign in
                </Link>
                <Link to="/register" className="home-cta home-cta--secondary">
                  Create account
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="home-hero__curve" aria-hidden="true" />
      </section>

      <section className="home-section home-features">
        <div className="home-container">
          <h2 className="home-section__title">For students</h2>
          <p className="home-section__subtitle">
            Everything you need to schedule lessons and follow your progress.
          </p>
          <ul className="home-features__grid">
            {studentFeatures.map(({ icon: Icon, title, description }) => (
              <li key={title} className="home-feature-card">
                <span className="home-feature-card__icon">
                  <Icon />
                </span>
                <h3 className="home-feature-card__title">{title}</h3>
                <p className="home-feature-card__desc">{description}</p>
              </li>
            ))}
          </ul>

          <div className="home-section">
            <h2 className="home-section__title">
              Instructors and administrators
            </h2>
            <p className="home-section__subtitle">
              The same portal gives instructors a calendar and session logs, and
              gives admins tools for users, payroll, and full school oversight.
            </p>
            <ul className="home-roles__grid home-roles__grid--compact">
              {otherRoles.map(({ icon: Icon, title, summary }) => (
                <li
                  key={title}
                  className="home-role-card home-role-card--compact"
                >
                  <span className="home-role-card__icon">
                    <Icon />
                  </span>
                  <h3 className="home-role-card__title">{title}</h3>
                  <p className="home-role-card__summary">{summary}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="home-section home-cta-section">
        <div className="home-container home-cta-section__inner">
          <h2 className="home-cta-section__title">Ready to get started?</h2>
          <p className="home-cta-section__text">
            Create an account to book your first lesson, or sign in if you
            already have one.
          </p>
          {!isAuthenticated && (
            <div className="home-cta-section__buttons">
              <Link to="/register" className="home-cta home-cta--primary">
                Create account
              </Link>
              <Link to="/login" className="home-cta home-cta--outline">
                Sign in
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
